
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import * as auraService from '@/services/auraPoints';
import { AuraPointsData, DEFAULT_ACHIEVEMENTS } from '@/models/AuraPoints';

// Context type definition
interface AuraPointsContextType {
  points: AuraPointsData | null;
  loading: boolean;
  error: Error | null;
  refreshPoints: () => Promise<void>;
}

// Create context
const AuraPointsContext = createContext<AuraPointsContextType | undefined>(undefined);

// Custom hook to use the context
export const useAuraPoints = () => {
  const context = useContext(AuraPointsContext);
  if (context === undefined) {
    throw new Error('useAuraPoints must be used within an AuraPointsProvider');
  }
  return context;
};

// Provider component
export const AuraPointsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [points, setPoints] = useState<AuraPointsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Function to fetch user's aura points
  const fetchUserPoints = async (userId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await auraService.getUserAuraPoints(userId);
      setPoints(data);
    } catch (err) {
      console.error('Error fetching aura points:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch aura points'));
      // Set default points data on error so UI can still work
      setPoints({
        totalPoints: 0,
        level: 1,
        ratingsReceived: 0,
        ratingsGiven: 0,
        averageRating: 0,
        lastUpdated: new Date().toISOString(),
        history: [],
        achievements: DEFAULT_ACHIEVEMENTS,
        streak: {
          currentStreak: 0,
          longestStreak: 0,
          lastActivityDate: null
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Refresh points data (can be called manually)
  const refreshPoints = async () => {
    if (currentUser) {
      await fetchUserPoints(currentUser.uid);
    }
  };

  // Fetch points when user changes
  useEffect(() => {
    if (currentUser) {
      fetchUserPoints(currentUser.uid);
    } else {
      setPoints(null);
      setLoading(false);
    }
  }, [currentUser]);

  // Context value
  const value: AuraPointsContextType = {
    points,
    loading,
    error,
    refreshPoints
  };

  return (
    <AuraPointsContext.Provider value={value}>
      {children}
    </AuraPointsContext.Provider>
  );
};
